import { useMemo, useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { CalendarDaysIcon, MoreHorizontalIcon, PlusIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ems/ui/components/dropdown-menu'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'
import { formatDateTime } from '@ems/ui/lib/format'

import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { sessionTone, type ProgramSession } from '#admin/features/program/api.ts'
import { useDeleteSession, useSetSessionStatus } from '#admin/features/program/mutations.ts'
import {
  roomsQuery,
  sessionsQuery,
  speakersQuery,
  tracksQuery,
} from '#admin/features/program/queries.ts'
import { SessionDialog } from '#admin/features/program/session-dialog.tsx'
import {
  availableSessionActions,
  isNeedsRoomRefusal,
  NEEDS_ROOM,
} from '#admin/features/program/sessions.ts'
import { label } from '#admin/features/registrations/api.ts'
import { TracksCard } from '#admin/features/program/tracks-card.tsx'
import { manifestQuery } from '#admin/features/tenant/queries.ts'
import { toastError } from '#admin/lib/toast-error.ts'
import { SessionsSkeleton } from '#admin/features/program/program-skeletons.tsx'
import {
  matchesSession,
  SESSION_FILTER_LABEL,
  SESSION_FILTERS,
  type SessionFilter,
} from '#admin/features/program/list-filters.ts'
import { FilteredEmpty, ListFilterBar } from '#admin/components/list-filter.tsx'
import { pageSlice, TablePagination, usePagination } from '#admin/components/table-pagination.tsx'

/**
 * The agenda: every session, drafts included, and the editor for them.
 *
 * **It loads `rundown` and `room`, and nothing else** — `rundown` requires `room`, so a tenant who
 * has the program has the venue. A loader reaching for a `business-matching` endpoint would turn
 * a module they never purchased into a hard failure on a page they did; the marketplace is on
 * `/program/meetings`, the rooms themselves on `/program/rooms`. The tracks are a card below
 * the sessions: two fields each, too small for a page.
 *
 * Without `rundown.manage` the write controls are removed and the notice says who to ask.
 */
export const Route = createFileRoute('/_authenticated/program/sessions')({
  pendingComponent: SessionsSkeleton,
  staticData: { crumb: { parents: [{ label: 'Program' }], label: 'Sessions' } },
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(sessionsQuery()),
      context.queryClient.ensureQueryData(roomsQuery()),
      context.queryClient.ensureQueryData(tracksQuery()),
      context.queryClient.ensureQueryData(speakersQuery()),
      context.queryClient.ensureQueryData(sessionQuery()),
      context.queryClient.ensureQueryData(manifestQuery()),
    ]),
  component: ProgramPage,
})

type Editing = {
  session: ProgramSession | null
  /** Publish was refused for want of a room: open on the room, and publish on save. */
  publishing?: boolean
} | null

function ProgramPage() {
  const { data: sessions } = useSuspenseQuery(sessionsQuery())
  const { data: rooms } = useSuspenseQuery(roomsQuery())
  const { data: tracks } = useSuspenseQuery(tracksQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const { data: manifest } = useSuspenseQuery(manifestQuery())
  const canManage = can(session, 'rundown.manage')

  const [editing, setEditing] = useState<Editing>(null)
  const [canceling, setCanceling] = useState<ProgramSession | null>(null)
  const [deleting, setDeleting] = useState<ProgramSession | null>(null)
  const { mutate: setStatus, isPending: moving } = useSetSessionStatus()
  const remove = useDeleteSession(() => setDeleting(null))

  const published = sessions.items.filter((s) => s.status === 'published').length

  // Filtered in the browser: the list is already here in full.
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<SessionFilter>('all')
  const roomNames = useMemo(
    () => new Map(rooms.items.map((room) => [room.id, room.name])),
    [rooms.items],
  )
  const shown = sessions.items.filter((s) => matchesSession(s, query, filter, roomNames))
  // One page at a time. A new search or pill starts again at the first page.
  const { page, setPage, pageSize, setPageSize } = usePagination(
    `${filter}|${query.trim().toLowerCase()}`,
  )
  const paged = pageSlice(shown, page, pageSize)

  const columns = useMemo<ColumnDef<ProgramSession, never>[]>(() => {
    const roomName = new Map(rooms.items.map((room) => [room.id, room.name]))

    const move = (target: ProgramSession, to: ProgramSession['status']) => {
      // Refused before it is sent: the dialog opens on the room, and saving publishes.
      if (to === 'published' && target.roomId === null) {
        setEditing({ session: target, publishing: true })
        return
      }
      if (to === 'canceled' && target.status === 'published') {
        setCanceling(target)
        return
      }
      setStatus(
        { session: target, status: to },
        {
          onError: (error) =>
            // The room was cleared by somebody else since the list loaded.
            isNeedsRoomRefusal(error)
              ? setEditing({ session: target, publishing: true })
              : toastError(error),
        },
      )
    }

    return [
      {
        accessorKey: 'title',
        header: 'Session',
        meta: { primary: true, wrap: true },
        // Two lines in the table, so the end of a long title (often the part that tells two
        // sessions apart) stays visible; the card below `md` shows it whole.
        cell: ({ row }) => (
          <span className="md:line-clamp-2 md:min-w-64 md:max-w-md" title={row.original.title}>
            {row.original.title}
          </span>
        ),
      },
      {
        id: 'startsAt',
        header: 'From',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{formatDateTime(row.original.startsAt)}</span>
        ),
      },
      {
        id: 'room',
        header: 'Room',
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.roomId === null
              ? 'Not placed'
              : (roomName.get(row.original.roomId) ?? 'Unknown room')}
          </span>
        ),
      },
      {
        id: 'speakers',
        header: 'Speakers',
        meta: { wrap: true },
        cell: ({ row }) => {
          const names = row.original.speakers.map((s) => s.name).join(', ')
          return (
            <span
              className="text-muted-foreground md:line-clamp-2 md:min-w-40 md:max-w-xs"
              title={names}
            >
              {names || '—'}
            </span>
          )
        },
      },
      {
        accessorKey: 'status',
        header: 'State',
        cell: ({ row }) => (
          <StatusBadge tone={sessionTone(row.original.status)}>
            {label(row.original.status)}
          </StatusBadge>
        ),
      },
      ...(canManage
        ? [
            {
              id: 'actions',
              header: () => <span className="sr-only">Actions</span>,
              meta: { align: 'end' as const },
              cell: ({ row }: { row: { original: ProgramSession } }) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Actions for ${row.original.title}`}
                    >
                      <MoreHorizontalIcon />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => setEditing({ session: row.original })}>
                      Edit
                    </DropdownMenuItem>
                    {availableSessionActions(row.original).map((action) => (
                      <DropdownMenuItem
                        key={action.to}
                        disabled={moving}
                        onSelect={() => move(row.original, action.to)}
                      >
                        {action.label}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      onSelect={() => setDeleting(row.original)}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            } satisfies ColumnDef<ProgramSession, never>,
          ]
        : []),
    ]
  }, [canManage, rooms.items, setStatus, moving])

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Sessions"
        description={`${published} of ${sessions.items.length} session${
          sessions.items.length === 1 ? '' : 's'
        } published`}
        actions={
          canManage ? (
            <Button onClick={() => setEditing({ session: null })}>
              <PlusIcon />
              Add session
            </Button>
          ) : null
        }
      />

      {canManage ? null : <ReadOnlyNotice area="the program" />}

      {sessions.items.length === 0 ? null : (
        <ListFilterBar
          query={query}
          onQueryChange={setQuery}
          placeholder="Search sessions by title, speaker, or room..."
          inputClassName="sm:max-w-sm"
          status={filter}
          onStatusChange={setFilter}
          statusLabel="Status"
          options={SESSION_FILTERS.map((value) => ({
            value,
            label: SESSION_FILTER_LABEL[value],
            count: sessions.items.filter((s) => value === 'all' || s.status === value).length,
          }))}
        />
      )}

      <DataTable
        caption="Program sessions"
        title="Sessions"
        description="Every session on the agenda, drafts included. Only published ones are public."
        // Only with rows: an empty table shows its empty state, and no line under it.
        footer={
          shown.length === 0 ? undefined : (
            <TablePagination
              page={paged.page}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalItems={shown.length}
              onPageChange={setPage}
              ariaLabel="Pages of the sessions"
            />
          )
        }
        columns={columns}
        data={paged.rows}
        getRowId={(s) => s.id}
        empty={
          sessions.items.length > 0 ? (
            <FilteredEmpty
              message={
                query.trim() === ''
                  ? 'No sessions in this status'
                  : `No sessions matching “${query.trim()}”`
              }
              onClear={() => {
                setQuery('')
                setFilter('all')
              }}
            />
          ) : (
            <EmptyState
              icon={<CalendarDaysIcon />}
              title="No sessions yet"
              description="Add the first session. It starts as a draft, so nothing is public until you publish it."
            />
          )
        }
      />

      <TracksCard tracks={tracks.items} sessions={sessions.items} canManage={canManage} />

      {editing === null || manifest === null ? null : (
        <SessionDialog
          // Keyed, so opening another session starts from its own values rather than the last edit.
          key={`${editing.session?.id ?? 'new'}${editing.publishing === true ? ':publish' : ''}`}
          session={editing.session}
          zone={manifest.tenant.timezone}
          rooms={rooms.items}
          tracks={tracks.items}
          publishing={editing.publishing ?? false}
          initialErrors={editing.publishing === true ? { roomId: NEEDS_ROOM } : {}}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={canceling !== null}
        onOpenChange={(open) => (open ? undefined : setCanceling(null))}
        title={`Cancel “${canceling?.title ?? ''}”?`}
        description="It comes off the public program. It stays here, marked canceled, and can be put back to draft."
        confirmLabel="Cancel session"
        destructive
        busy={moving}
        onConfirm={() => {
          if (canceling === null) return
          setStatus(
            { session: canceling, status: 'canceled' },
            { onError: toastError, onSettled: () => setCanceling(null) },
          )
        }}
      />

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => (open ? undefined : setDeleting(null))}
        title={`Delete “${deleting?.title ?? ''}”?`}
        description="It disappears from this list and from the public program."
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onConfirm={() => (deleting === null ? undefined : remove.mutate(deleting))}
      />
    </div>
  )
}
